import PropTypes from 'prop-types';

/** @jsx jsx */
import { jsx } from '@emotion/core';

import { StructFlex, StructItem } from '@cybs-ui/components/lib/struct-flex';
import CybsButton from '@cybs-ui/components/lib/button';
import CybsKeyValuePair from '@cybs-ui/components/lib/key-value-pair';
import CybsStatusBadge from '@cybs-ui/components/lib/status-badge';

// Styles
import styles from './component.styles';

// Components
import BreadCrumb from './components/breadcrumb.component';

const bgStyles = theme => styles(theme).bg;
const pageTitleStyles = theme => styles(theme).pageTitle;
const helpTextStyles = theme => styles(theme).helpText;
const navWrapper = theme => styles(theme).nav;
const backButtonStyles = theme => styles(theme).backButton;
const rightButtonStyles = theme => styles(theme).rightButtonStyle;

const CybsHeader = ({
  buttons,
  breadCrumbs,
  hasBackButton,
  metaData,
  dataTest,
  i18n: i18nUser,
  onBackButtonClick
}) => {
  const i18n = {
    ...CybsHeader.defaultProps.i18n,
    ...i18nUser
  };

  // eslint-disable-next-line react/display-name
  const metaDataValueRenderer = value => {
    switch (value.type) {
      case 'link':
        return (
          <CybsButton
            href={value.href}
            i18n={{ label: value.label }}
            onClick={value.onClick}
            tag="a"
            variant="link"
            colorScheme="secondary"
          />
        );
      case 'status':
        return (
          <CybsStatusBadge
            status={value.status}
            label={value.label}
            dataTest={value.dataTest}
          />
        );
      default:
        return value.label;
    }
  };

  return (
    <StructFlex
      css={bgStyles}
      align-items="flex-start"
      justify-content="space-between"
      data-test={`${dataTest}-cybsHeader`}
    >
      <StructItem direction="column">
        {breadCrumbs && breadCrumbs.length > 0 && (
          <StructItem align-items="center">
            <nav aria-label={i18n.ariaLabel} css={navWrapper}>
              <ol>{breadCrumbs.map(BreadCrumb)}</ol>
            </nav>
          </StructItem>
        )}
        <StructFlex align-items="center">
          <h1 css={pageTitleStyles}>{i18n.title}</h1>
          {hasBackButton && (
            <CybsButton
              css={backButtonStyles}
              colorScheme="secondary"
              dataTest={`${dataTest}-back-button`}
              i18n={{ label: i18n.backButtonLabel }}
              iconClass="arrow-left"
              onClick={onBackButtonClick}
              variant="icon"
            />
          )}
        </StructFlex>
        {metaData && (
          <div>
            {metaData.map(metaDataItem => (
              <CybsKeyValuePair
                dataTest={metaDataItem.value.dataTest || ''}
                label={metaDataItem.label}
                value={metaDataValueRenderer(metaDataItem.value)}
              />
            ))}
          </div>
        )}
        {i18n.description && (
          <div>{<p css={helpTextStyles}>{i18n.description}</p>}</div>
        )}
      </StructItem>
      <StructFlex align-items="center" flex-wrap="wrap" css={rightButtonStyles}>
        {buttons &&
          buttons.length > 0 &&
          buttons.map(
            ({
              advancedDropdown,
              colorScheme,
              iconClass,
              label,
              onClick,
              type,
              variant,
              ...rest
            }) =>
              type === 'advanced-dropdown' && advancedDropdown ? (
                advancedDropdown
              ) : (
                <CybsButton
                  dataTest={dataTest}
                  colorScheme={colorScheme}
                  iconClass={iconClass}
                  i18n={{ label }}
                  key={`${label || iconClass}-${Math.random().floor}`}
                  onClick={onClick}
                  variant={variant}
                  {...rest}
                />
              )
          )}
      </StructFlex>
    </StructFlex>
  );
};

CybsHeader.defaultProps = {
  buttons: [],
  breadCrumbs: [],
  hasBackButton: false,
  metaData: null,
  onBackButtonClick: () => {},
  i18n: {
    ariaLabel: 'Breadcrumbs',
    backButtonLabel: 'Back',
    description: null
  }
};

CybsHeader.propTypes = {
  /**
   * Locale
   */
  i18n: PropTypes.shape({
    ariaLabel: PropTypes.string,
    backButtonLabel: PropTypes.string,
    description: PropTypes.string,
    title: PropTypes.string.isRequired
  }),
  /**
   * Header buttons
   */
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      advancedDropdown: PropTypes.element,
      colorScheme: PropTypes.string,
      iconClass: PropTypes.string,
      onClick: PropTypes.func,
      label: PropTypes.string,
      type: PropTypes.string,
      variant: PropTypes.string
    })
  ),
  /**
   * dataTest
   */
  dataTest: PropTypes.string.isRequired,
  /**
   * header route-history anchor tags
   */
  breadCrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      pageTitle: PropTypes.string.isRequired,
      onClick: PropTypes.func
    })
  ),
  /**
   * Boolean for showing a back button
   */
  hasBackButton: PropTypes.bool,
  /**
   * Static data displayed below title
   */
  metaData: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.shape({
        href: PropTypes.string,
        label: PropTypes.string.isRequired,
        status: PropTypes.string,
        type: PropTypes.string.isRequired,
        dataTest: PropTypes.string.isRequired,
        onClick: PropTypes.func
      })
    })
  ),
  /**
   * Call back when clicking back button
   */
  onBackButtonClick: PropTypes.func
};

CybsHeader.displayName = 'CybsHeader';

export default CybsHeader;
